import React, { useMemo, useState } from "react";
import { Network } from "lucide-react";
import DragNumberInput from "../ui/DragNumberInput";
import MenuSelect from "../ui/MenuSelect";
import Separator from "../ui/Separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/Collapsible";
import OffsetEditor from "./OffsetEditor";
import {
  useBodies,
  useSceneActions,
  useSites,
  useTendons,
  useWorldBodies,
} from "../../stores/sceneStore";
import { useSelectionStore } from "../../stores/selectionStore";
import { findGeomNode } from "../../utils/geomSelection";
import { eulerDegreesToQuaternion, quaternionToEulerDegrees } from "../../core/transform";
import { requestInspectorChromeFlash } from "../../utils/inspectorChromeFlash";
import { getWorldBodyLabelForBodyUuid } from "../../utils/worldBodyLabels";
import { getTransformEditorBlockProps, usePhysicsAuthoringLock } from "../../utils/physicsGuards";
import { stripNamespacePrefix as stripPrefix } from "../../utils/nameUtils";

const isUuidLike = (value) =>
  typeof value === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());

const WRAP_TYPE_OPTIONS = [
  { value: "sphere", label: "Sphere" },
  { value: "cylinder", label: "Cylinder" },
];

const getSizeLabels = (kind) =>
  kind === "sphere"
    ? [{ label: "Radius", index: 0 }]
    : [
        { label: "Radius", index: 0 },
        { label: "Height", index: 1 },
      ];

const clampFinite = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export default function TendonWrapGeomPropertiesPanel() {
  const selectedGeom = useSelectionStore((state) => state.selectedGeom);
  const selectTendon = useSelectionStore((state) => state.selectTendon);
  const setLastSelectionSource = useSelectionStore((state) => state.setLastSelectionSource);
  const bodies = useBodies();
  const worldBodies = useWorldBodies();
  const tendons = useTendons();
  const sites = useSites();
  const sceneActions = useSceneActions();
  const authoringLocked = usePhysicsAuthoringLock();
  const transformEditorBlock = getTransformEditorBlockProps(authoringLocked);
  const resolved = useMemo(
    () => (selectedGeom ? findGeomNode(bodies, selectedGeom) : null),
    [bodies, selectedGeom]
  );
  const body = resolved?.body || null;
  const bodyUuid = resolved?.bodyUuid || null;
  const geom = resolved?.geom || null;
  const geomUuid = geom?.uuid ? String(geom.uuid) : "";
  const geomKey = bodyUuid && geomUuid ? `${bodyUuid}:${geomUuid}` : "";
  const objectLabel = useMemo(
    () => (bodyUuid ? getWorldBodyLabelForBodyUuid(bodyUuid, bodies, worldBodies) : null),
    [bodyUuid, bodies, worldBodies]
  );

  const routeRefs = useMemo(() => {
    if (!geom) return [];
    const names = new Set([String(geom.uuid || ""), String(geom.name || "")].filter(Boolean));
    const refs = [];
    Object.values(tendons || {}).forEach((tendon) => {
      if (!tendon || tendon.kind !== "spatial" || !Array.isArray(tendon.spatial)) return;
      tendon.spatial.forEach((entry, routeIndex) => {
        if (entry?.kind !== "geom" || !names.has(String(entry.geom || ""))) return;
        const sideSite = entry.sidesite ? sites?.[entry.sidesite] : null;
        refs.push({
          tendon,
          routeIndex,
          sidesiteLabel: stripPrefix(sideSite?.name || entry.sidesite || ""),
        });
      });
    });
    return refs;
  }, [geom, sites, tendons]);

  const [nameDraft, setNameDraft] = useState("");
  const [editingName, setEditingName] = useState(false);

  if (!geom || !bodyUuid || !geomKey) return null;

  const kind = String(geom.kind || geom.type || "sphere").toLowerCase();
  const position = Array.isArray(geom.position) ? geom.position : [0, 0, 0];
  const orientation = Array.isArray(geom.orientation) ? geom.orientation : [1, 0, 0, 0];
  const rotationDeg = quaternionToEulerDegrees(orientation);
  const size = Array.isArray(geom.size) ? geom.size : [0.0035, 0.006, 0];
  const bodyName = stripPrefix(body?.name);
  const objectName = stripPrefix(objectLabel);
  const parentBodyLabel =
    bodyName && bodyName !== bodyUuid && !isUuidLike(bodyName)
      ? bodyName
      : objectName && objectName !== bodyUuid && !isUuidLike(objectName)
        ? objectName
        : "Unnamed body";

  const updatePosition = (axis, value) => {
    const next = [...position];
    next[axis] = clampFinite(value, next[axis] || 0);
    sceneActions.setGeomPosition(bodyUuid, geomUuid, next);
  };

  const updateRotation = (axis, value) => {
    const next = [...rotationDeg];
    next[axis] = clampFinite(value, next[axis] || 0);
    sceneActions.setGeomOrientation(bodyUuid, geomUuid, eulerDegreesToQuaternion(next));
  };

  const updateQuaternion = (axis, value) => {
    const next = [...orientation];
    next[axis] = clampFinite(value, next[axis] || 0);
    sceneActions.setGeomOrientation(bodyUuid, geomUuid, next);
  };

  const updateSize = (axis, value) => {
    const next = [...size];
    next[axis] = Math.max(0, clampFinite(value, next[axis] || 0));
    sceneActions.setGeomSize(bodyUuid, geomUuid, next);
  };

  const updateKind = (nextKind) => {
    if (!nextKind || nextKind === kind) return;
    const nextSize =
      nextKind === "sphere" ? [size[0] || 0.0035, 0, 0] : [size[0] || 0.0035, size[1] || 0.006, 0];
    sceneActions.setGeomKindAndSize(bodyUuid, geomUuid, nextKind, nextSize);
  };

  const commitName = () => {
    const nextName = nameDraft.trim();
    if (nextName && nextName !== geom.name) {
      sceneActions.setGeomName(bodyUuid, geomUuid, nextName);
    }
    setEditingName(false);
  };

  const focusTendon = (tendonUuid) => {
    if (!tendonUuid) return;
    setLastSelectionSource("sceneTree");
    selectTendon(tendonUuid);
    requestInspectorChromeFlash();
  };

  return (
    <div className="p-3 space-y-4">
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wider panel-text-helper">Tendon Wrap Geom</div>
        <div className="rounded border border-gray-700 bg-gray-900/40 p-2 text-xs text-gray-400">
          Parent body: <span className="text-gray-200">{parentBodyLabel}</span>
        </div>
      </div>

      <Separator />

      <Collapsible defaultOpen>
        <CollapsibleTrigger quiet>Identity</CollapsibleTrigger>
        <CollapsibleContent>
          <div className="pt-2 space-y-3">
            <div className="space-y-1">
              <label className="text-xs text-gray-400" htmlFor={`wrap-name-${geomUuid}`}>
                Name
              </label>
              <input
                id={`wrap-name-${geomUuid}`}
                value={editingName ? nameDraft : geom.name || ""}
                onFocus={() => {
                  setNameDraft(geom.name || "");
                  setEditingName(true);
                }}
                onChange={(event) => setNameDraft(event.target.value)}
                onBlur={commitName}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.currentTarget.blur();
                  if (event.key === "Escape") setEditingName(false);
                }}
                className="w-full rounded border border-gray-700 bg-gray-800 px-2 py-1 text-xs text-gray-100"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-gray-400" htmlFor={`wrap-type-${geomUuid}`}>
                Type
              </label>
              <MenuSelect
                size="compact"
                value={kind === "cylinder" ? "cylinder" : "sphere"}
                onChange={updateKind}
                options={WRAP_TYPE_OPTIONS}
                triggerProps={{ id: `wrap-type-${geomUuid}` }}
                aria-label="Wrap geom type"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {getSizeLabels(kind).map(({ label, index }) => (
                <DragNumberInput
                  key={label}
                  label={label}
                  value={size[index] || 0}
                  dragValue={size[index] || 0}
                  deferDragUpdates={false}
                  onChange={(event) => updateSize(index, event.target.value)}
                  onDragValueChange={(value) => updateSize(index, value)}
                  wrapperClassName="flex items-center gap-1"
                  inputClassName="min-w-0 flex-1 px-2 py-1 text-xs border border-gray-600 rounded bg-gray-800 text-gray-100"
                  labelClassName="text-xs text-gray-300 select-none shrink-0"
                  inputProps={{ step: "0.01" }}
                />
              ))}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      <OffsetEditor
        title="Transform"
        globalPosition={position}
        localPosition={position}
        localRotationDeg={rotationDeg}
        quaternion={orientation}
        onPositionChange={updatePosition}
        onRotationChange={updateRotation}
        onQuaternionChange={updateQuaternion}
        physicsAuthoringLocked={authoringLocked}
        disabled={transformEditorBlock.disabled}
        disabledTitle={transformEditorBlock.title}
      />

      <Collapsible defaultOpen>
        <CollapsibleTrigger quiet>Route References</CollapsibleTrigger>
        <CollapsibleContent>
          <div className="pt-2 space-y-1">
            {routeRefs.length === 0 ? (
              <div className="rounded border border-gray-700 bg-gray-900/40 p-2 text-xs panel-text-helper">
                No spatial tendon route currently references this geom.
              </div>
            ) : (
              routeRefs.map((ref) => (
                <button
                  key={`${ref.tendon.uuid}-${ref.routeIndex}`}
                  type="button"
                  onClick={() => focusTendon(ref.tendon.uuid)}
                  className="flex w-full items-center gap-2 rounded border border-gray-700 bg-gray-900/40 px-2 py-1.5 text-left text-xs text-gray-300 hover:border-gray-500 hover:text-gray-100"
                >
                  <Network className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate">
                    {stripPrefix(ref.tendon.name) || ref.tendon.uuid} / route {ref.routeIndex + 1}
                    {ref.sidesiteLabel ? ` / side ${ref.sidesiteLabel}` : ""}
                  </span>
                </button>
              ))
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
